import { Form } from 'antd';
import { FormInstance, useForm } from 'antd/lib/form/Form';
import { uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { IAdminMemberItem } from '.';
import { saveSupportRoleUsers } from '../../../../api/admin/rbac';
import { ConsoleDrawer } from '../../../../components/drawer';
import { ConsoleForm } from '../../../../components/form';
import { messageError } from '../../../../components/message';
import getFormatMessage from '../../../../locales/get-format-message';
import { RbacRoleType } from '../../../../model';
import { IApiAdminRbacRole } from '../../../../model/api-modules';
import { NOTICE_ADMIN_ROLE } from '../role';
import { CommonMultiSelect } from '../../../../components/select/checkbox-select';

let editRoleIds: number[] = [];

interface IDrawerProps {
  roles: readonly IApiAdminRbacRole[];
  operateUser: IAdminMemberItem;
  isSuper: boolean;
  operator: string;
  onSave: () => void;
  onCancel: () => void;
}

export const EditUserDrawer = (props: IDrawerProps) => {
  const { roles, operateUser, isSuper, operator, onSave, onCancel } = props;
  const [form] = useForm();
  const content = (
    <EditForm
      form={form}
      operateUser={operateUser}
      roles={roles}
      isSuper={isSuper}
    />
  );
  return (
    <ConsoleDrawer
      {...{
        title: getFormatMessage('edit_member'),
        okText: getFormatMessage('Confirm'),
        cancelText: getFormatMessage('cancel'),
        children: content,
        onOk: async () => {
          const userRoleIds = roles
            .filter((role) => role.userList.includes(operateUser.email))
            .map((role) => role.roleId);

          await form.validateFields();

          const needUpdateRoles = roles
            .filter((role) =>
              [...editRoleIds, ...userRoleIds].includes(role.roleId)
            )
            .map((role) => {
              const isSelectRole = editRoleIds.includes(role.roleId);
              if (isSelectRole) {
                return {
                  roleId: role.roleId,
                  operator,
                  userIdList: uniq(role.userList.concat(operateUser.email)),
                };
              }
              return {
                roleId: role.roleId,
                operator,
                userIdList: role.userList.filter(
                  (email) => email !== operateUser.email
                ),
              };
            });
          for (const matchedRole of needUpdateRoles) {
            const res = await saveSupportRoleUsers(matchedRole);
            if (!res) {
              messageError(getFormatMessage('failed'));
              return false;
            }
          }
          onSave();
          return true;
        },
        onClose: onCancel,
      }}
    ></ConsoleDrawer>
  );
};

interface IProps {
  form: FormInstance<any>;
  operateUser: IAdminMemberItem;
  roles: readonly IApiAdminRbacRole[];
  isSuper: boolean;
}

const EditForm = (props: IProps) => {
  const { form, operateUser, roles, isSuper } = props;

  const roleOptions = roles
    .filter((role) =>
      isSuper
        ? ![RbacRoleType.Owner].includes(role.type)
        : ![RbacRoleType.Owner, RbacRoleType.Admin].includes(role.type)
    )
    .map((role) => {
      return {
        label: role.roleName,
        value: role.roleId,
      };
    });
  const userRoleIds = roles
    .filter((role) => role.userList.includes(operateUser.email))
    .map((role) => role.roleId);
  const [selectRoleIds, setSelectRoleIds] = useState<number[]>(userRoleIds);

  useEffect(() => {
    editRoleIds = userRoleIds;
  }, []);

  useEffect(() => {
    form.setFieldValue('roles', selectRoleIds);
  }, [selectRoleIds]);

  const handleRoleChange = (values: number[]) => {
    const adminRole = roles.find((item) => item.type === RbacRoleType.Admin);
    if (!adminRole) {
      return;
    }

    editRoleIds = values;
    const lastEditRole = roles.find(
      (role) => role.roleId === [...values].pop()
    );
    if (lastEditRole?.roleName === NOTICE_ADMIN_ROLE) {
      setSelectRoleIds(values);
      return;
    }
    if (lastEditRole?.roleId === adminRole.roleId) {
      const noticeRole = roles
        .filter((role) => values.includes(role.roleId))
        .find((role) => role.roleName === NOTICE_ADMIN_ROLE);
      editRoleIds = noticeRole
        ? [adminRole.roleId, noticeRole.roleId]
        : [adminRole.roleId];
    } else {
      editRoleIds = values.filter((id) => adminRole.roleId !== id);
    }
    setSelectRoleIds(editRoleIds);
  };
  return (
    <ConsoleForm form={form}>
      <Form.Item label={getFormatMessage('member_name')}>
        {operateUser.userName}
      </Form.Item>
      <Form.Item label={getFormatMessage('email_uppercase')}>
        {operateUser.email}
      </Form.Item>
      <Form.Item
        label={getFormatMessage('role')}
        rules={[{ required: true }]}
        name="roles"
      >
        <CommonMultiSelect options={roleOptions} onChange={handleRoleChange} />
      </Form.Item>
    </ConsoleForm>
  );
};
